import {
  resetRefiRequestStatus,
  trackRefiDetailsViewEvent,
} from '../../../actions/refinance'
import { useIntl } from 'react-intl'
import { OverlayPanel, Page } from '../..'
import { useState, useEffect } from 'react'
import { resetTca } from '../../../actions/tca'
import RefiDetailsBody from './RefiDetailsBody'
import RefiDetailsHeader from './RefiDetailsHeader'
import RefiDetailsFooter from './RefiDetailsFooter'
import { RefiDetail } from '../../../types/refinance'
import { SlideOnMount } from '../../shared/Animations'
import { useDispatch, useSelector } from 'react-redux'
import { getCurrentPath } from '../../../helpers/browser'
import { buildRefiKey } from '../../../constants/refinance'
import { OVERLAY_HELPERS } from '../../../constants/navigation'
import { selectRefiYearsInHome } from '../../../store/selectors/refinance'
import { filterMortgageRatesAndSort } from '../../../helpers/mortgageRates'

import styles from './RefiDetailsOverlay.module.scss'

const FOOTER_ID = 'refi-panel-footer'

export interface RefiDetailsOverlayProps {
  options: RefiDetail[]
}

export const RefiDetailsOverlay = ({ options }: RefiDetailsOverlayProps) => {
  const intl = useIntl()
  const origin = OVERLAY_HELPERS.refinance.getOrigin()
  const yearsInHome = useSelector(selectRefiYearsInHome)
  const filteredRefiOptions = filterMortgageRatesAndSort(options)
  const overlay = `${origin}/${OVERLAY_HELPERS.refinance.overlay}`
  const [prevNumber, setPrevNumber] = useState(options[0].longterm)
  const title = OVERLAY_HELPERS.getTitle('refinance', intl.formatMessage)
  const [disablePanelAnimation, setDisablePanelAnimation] = useState(false)

  return (
    <OverlayPanel
      back={origin}
      path={overlay}
      title={title}
      onOpen={() => setTimeout(() => setDisablePanelAnimation(true), 500)}
      onClose={() => setDisablePanelAnimation(false)}
      render={({ match, history }: any) => {
        const refiPages = filteredRefiOptions.map((option, index) => (
          <Page
            key={buildRefiKey(option)}
            path={`${match.url}/${buildRefiKey(option)}`}
            navigation={{
              swipeEnabled: true,
              onPrev: () => {
                const prevIndex =
                  (filteredRefiOptions.length + index - 1) %
                  filteredRefiOptions.length
                setPrevNumber(option.longterm)
                history.replace(
                  `${match.url}/${buildRefiKey(filteredRefiOptions[prevIndex])}`
                )
              },
              onNext: () => {
                const nextIndex = (index + 1) % filteredRefiOptions.length
                setPrevNumber(option.longterm)
                history.replace(
                  `${match.url}/${buildRefiKey(filteredRefiOptions[nextIndex])}`
                )
              },
            }}
            render={() => (
              <RefiDetailsPageBody
                options={filteredRefiOptions}
                option={option}
                index={index}
                yearsInHome={yearsInHome}
                prevNumber={prevNumber}
                disableAnimation={disablePanelAnimation}
                footerId={FOOTER_ID}
              />
            )}
          />
        ))
        const selectedOption = filteredRefiOptions.find(option =>
          getCurrentPath().endsWith(buildRefiKey(option))
        )
        const footer = (
          <Page.Footer className={styles.footer} key='refi_panel_footer'>
            <RefiDetailsFooter id={FOOTER_ID} {...selectedOption} />
          </Page.Footer>
        )

        return [...refiPages, footer]
      }}
    />
  )
}

export interface RefiDetailsPageBodyProps {
  index: number
  footerId: string
  option: RefiDetail
  prevNumber?: number
  yearsInHome: number
  options: RefiDetail[]
  disableAnimation: boolean
}

export const RefiDetailsPageBody = ({
  options,
  option,
  index,
  yearsInHome,
  prevNumber,
  disableAnimation,
  footerId,
}: RefiDetailsPageBodyProps) => {
  const dispatch = useDispatch()
  useEffect(() => {
    dispatch(trackRefiDetailsViewEvent(option))
    dispatch(resetRefiRequestStatus())
    dispatch(resetTca())
  }, [option, dispatch])

  return (
    <Page.Body>
      <SlideOnMount disabled={disableAnimation}>
        <Page.Carousel indicateIndex={index} indicateTotal={options.length}>
          <RefiDetailsHeader
            {...option}
            yearsInHome={yearsInHome}
            prevNumber={prevNumber}
          />
        </Page.Carousel>
      </SlideOnMount>
      <RefiDetailsBody
        disableAnimation={disableAnimation}
        yearsInHome={yearsInHome}
        footerId={footerId}
        {...option}
      />
      <Page.Disclaimer />
    </Page.Body>
  )
}
